import React, { useCallback, useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const ALLOWED_MODULES = [4, 10];

const fmtDate = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export default function DepartmentalRoles() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);

  const [departments, setDepartments] = useState([]);
  const [employees,   setEmployees]   = useState([]);

  const [departmentID, setDepartmentID] = useState("");
  const [employeeID,   setEmployeeID]   = useState("");
  const [fromDate,     setFromDate]     = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [history,        setHistory]        = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedDeptName, setSelectedDeptName] = useState("");

  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) return;

    fetch(`${API_ROOT}/api/directory/departments`)
      .then(r => r.json())
      .then(data => setDepartments(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch(`${API_ROOT}/api/employees/departmental-roles/employees`)
      .then(r => r.json())
      .then(data => setEmployees(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHistory = useCallback(async (deptId) => {
    if (!deptId) { setHistory([]); return; }
    setHistoryLoading(true);
    try {
      const res  = await fetch(`${API_ROOT}/api/employees/departmental-roles/history?departmentID=${deptId}`);
      const data = await res.json();
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleDeptChange = (e) => {
    const val = e.target.value;
    setDepartmentID(val);
    const dept = departments.find(d => String(d.DepartmentID) === val);
    setSelectedDeptName(dept ? dept.Department : "");
    fetchHistory(val);
  };

  if (!ALLOWED_MODULES.includes(moduleId)) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!departmentID || !employeeID || !fromDate) {
      setMessage({ text: "Invalid input. Please fill all fields correctly.", type: "danger" });
      return;
    }
    setLoading(true);
    setMessage({ text: "", type: "" });
    try {
      const res  = await fetch(`${API_ROOT}/api/employees/departmental-roles/save`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ employeeID, departmentID, fromDate }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ text: data.message || "Saved.", type: "success" });
        setEmployeeID(""); setFromDate("");
        fetchHistory(departmentID);
      } else {
        setMessage({ text: data.message || "Error saving.", type: "danger" });
      }
    } catch {
      setMessage({ text: "Error saving department role.", type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <h4 className="ctitle">Departmental Roles</h4>

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })} />
        </div>
      )}

      <form onSubmit={handleSave}>
        <table style={{ borderCollapse: "collapse", width: "auto", border: "0" }}>
          <tbody>
            <tr style={{ border: "0" }}>
              <td style={{ border: "0", padding: "0 8px 4px 0" }}><label className="form-label fw-semibold mb-0">Select Department</label></td>
              <td style={{ border: "0", padding: "0 8px 4px 0" }}><label className="form-label fw-semibold mb-0">Select Employee</label></td>
              <td style={{ border: "0", padding: "0 8px 4px 0" }}><label className="form-label fw-semibold mb-0">From Date</label></td>
              <td style={{ border: "0", padding: "0" }}></td>
            </tr>
            <tr style={{ border: "0" }}>
              <td style={{ border: "0", padding: "0 8px 0 0" }}>
                <select
                  className="form-select form-select-sm"
                  value={departmentID}
                  onChange={handleDeptChange}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map(d => (
                    <option key={d.DepartmentID} value={d.DepartmentID}>{d.Department}</option>
                  ))}
                </select>
              </td>
              <td style={{ border: "0", padding: "0 8px 0 0" }}>
                <select
                  className="form-select form-select-sm"
                  value={employeeID}
                  onChange={e => setEmployeeID(e.target.value)}
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.EmployeeID} value={emp.EmployeeID}>
                      {emp.EmpName} — {emp.Department} — {emp.Designation}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ border: "0", padding: "0 8px 0 0" }}>
                <input
                  className="form-control form-control-sm"
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  required
                />
              </td>
              <td style={{ border: "0", padding: "0", verticalAlign: "bottom" }}>
                <button className="btn btn-success btn-sm" type="submit" disabled={loading} style={{ padding: "5px 24px" }}>
                  {loading ? "Saving..." : "Save"}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      {departmentID && (
        <div className="mt-4">
          <h6 className="fw-semibold mb-2">
            Departmental Heads — {selectedDeptName}
          </h6>
          {historyLoading ? (
            <div className="text-muted">Loading...</div>
          ) : (
            <table className="table table-bordered table-sm align-middle" style={{ width: "auto" }}>
              <thead style={{ "--bs-table-bg": "#008080", color: "white" }}>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>From Date</th>
                  <th>To Date</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-muted">No records found.</td></tr>
                ) : (
                  history.map((row, idx) => (
                    <tr key={idx} className={row.ToDate == null ? "table-success fw-semibold" : ""}>
                      <td>{idx + 1}</td>
                      <td>{row.EmpName}</td>
                      <td>{row.Designation}</td>
                      <td>{fmtDate(row.FromDate)}</td>
                      <td>{row.ToDate == null ? <span className="badge bg-success">Current</span> : fmtDate(row.ToDate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
